import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';

export type Presentacion = components['schemas']['PresentationDetail'];
export type Diapositiva = components['schemas']['Slide'];

const clave = (id: number) => ['contenido', 'presentacion', id];
const ruta = (id: number) => ({ params: { path: { id: String(id) } } });

export function usePresentacion(id: number) {
  return useQuery({
    queryKey: clave(id),
    queryFn: () => unwrap<Presentacion>(api.GET('/api/v1/admin/presentations/{id}', ruta(id))),
  });
}

/** Mutaciones de la presentación; las de diapositivas reemplazan la lista en caché. */
export function usePresentacionMutaciones(id: number) {
  const qc = useQueryClient();
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const ponerDiapos = (slides: Diapositiva[]) => {
    qc.setQueryData<Presentacion>(clave(id), (p) => (p ? { ...p, slides } : p));
    void qc.invalidateQueries({ queryKey: clave(id) });
  };
  const hijo = (childId: number) => ({
    params: { path: { id: String(id), childId: String(childId) } },
  });
  return {
    guardar: useMutation({
      mutationFn: (body: components['schemas']['PresentationUpdateBody']) =>
        unwrap<Presentacion>(api.PATCH('/api/v1/admin/presentations/{id}', { ...ruta(id), body })),
      onSuccess: (d) => {
        qc.setQueryData(clave(id), d);
        void qc.invalidateQueries({ queryKey: ['contenido', 'biblioteca'] });
        toast.success('Presentación guardada.');
      },
      onError: err,
    }),
    agregar: useMutation({
      mutationFn: (body: components['schemas']['SlidesAddBody']) =>
        unwrap<Diapositiva[]>(
          api.POST('/api/v1/admin/presentations/{id}/slides', { ...ruta(id), body }),
        ),
      onSuccess: ponerDiapos,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({
        childId,
        body,
      }: {
        childId: number;
        body: components['schemas']['SlideUpdateBody'];
      }) =>
        unwrap<Diapositiva[]>(
          api.PATCH('/api/v1/admin/presentations/{id}/slides/{childId}', {
            ...hijo(childId),
            body,
          }),
        ),
      onSuccess: ponerDiapos,
      onError: err,
    }),
    quitar: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<Diapositiva[]>(
          api.POST('/api/v1/admin/presentations/{id}/slides/remove', {
            ...ruta(id),
            body: { ids },
          }),
        ),
      onSuccess: ponerDiapos,
      onError: err,
    }),
    ordenar: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<Diapositiva[]>(
          api.PUT('/api/v1/admin/presentations/{id}/slides/order', { ...ruta(id), body: { ids } }),
        ),
      onSuccess: ponerDiapos,
      onError: err,
    }),
    alfabetico: useMutation({
      mutationFn: () =>
        unwrap<Diapositiva[]>(
          api.POST('/api/v1/admin/presentations/{id}/slides/sort-alphabetically', ruta(id)),
        ),
      onSuccess: ponerDiapos,
      onError: err,
    }),
    borrar: useMutation({
      mutationFn: () => unwrap(api.DELETE('/api/v1/admin/presentations/{id}', ruta(id))),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
    }),
  };
}
